import React from 'react'
import { TripGenerationMode } from '@/app/constants/delegation/tripGenerationMode'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import DividerVertical from '@/modules/drivers/shared/DividerVertical'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import {
  faCalendarDaysSolid,
  faCalendarRangeSolid,
  faCalendarWeekSolid,
  faClockDeskSolid,
  faEarthAmericasSolid,
  faHouseSolid,
  FaIcon,
  faTruckSolid,
} from '@/lib/fa'
import { faGlobe, faKeyboard } from '@fortawesome/free-solid-svg-icons'
import TmsWhite from '@/assets/icons_new/TmsWhite.svg'
import DelegationMarker from './markers/DelegationMarker'

const MARKER_TYPES = [
  TripEventType.Begin,
  TripEventType.Finish,
  TripEventType.ChangeCountry,
  TripEventType.TripExclusionStart,
  TripEventType.TripExclusionEnd,
  TripEventType.Arrive,
  TripEventType.Leave,
  TripEventType.Loading,
  TripEventType.Unloading,
]

const newRow: EventModel = {
  tripEvent: TripEventType.NotSet,
  vehicleType: TripVehicleType.NotSet,
  city: '',
  comments: '',
  date: undefined,
  isTransit: false,
  registrationNumber: '',
  isChecked: false,
  lp: -1,
  country: { code: 'pl', name: 'Polska', nationId: 40, id: '1' },
}

const canDragMarker = (
  eventType: TripEventType,
  events: Array<any>,
  groupMode: boolean,
): boolean => {
  if (!groupMode) {
    if (eventType === TripEventType.Begin) {
      return !events.some((e) => e.tripEvent === TripEventType.Begin)
    }
    if (eventType === TripEventType.Finish) {
      return !events.some((e) => e.tripEvent === TripEventType.Finish)
    }
  }
  return true
}

const CalendarIconWithBadge = ({ badge }: { badge: string }) => (
  <div className="tw-relative tw-inline-flex">
    <FaIcon
      icon={faCalendarDaysSolid}
      className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
    />
    <div className="tw-absolute -tw-right-1 -tw-top-1 tw-flex tw-h-3 tw-w-3 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#2448bb]">
      <span className="tw-text-[8px] tw-font-medium tw-leading-none tw-text-white">
        {badge}
      </span>
    </div>
  </div>
)

const ImportButton = ({
  text,
  onClick,
  children,
}: {
  text: string
  onClick: () => void
  children: React.ReactNode
}) => (
  <Tooltip text={text} onClick={onClick}>
    <div className="tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-md tw-bg-primary/30 tw-p-1 hover:tw-bg-primary/50">
      {children}
    </div>
  </Tooltip>
)

export interface BusinessTripActionBarProps {
  events: Array<any>
  /** In group mode any number of Begin/Finish markers may be dropped. */
  groupMode?: boolean
  readonly?: boolean
  isRestsOnBaseModeOn: boolean
  setIsRestsOnBaseModeOn: React.Dispatch<React.SetStateAction<boolean>>
  /** Import sources - each icon shows only when its handler is provided. */
  onShowTmsLoads?: () => void
  onShowCardLoads?: () => void
  onShowBorders?: () => void
  onShowGpsView?: () => void
  onShowTmsView?: () => void
  onShowTachographView?: () => void
  onShowEvents?: () => void
  onGenerateTrips?: (generationMode: number) => void | Promise<void>
}

const BusinessTripActionBar = ({
  events,
  groupMode = true,
  readonly = false,
  isRestsOnBaseModeOn,
  setIsRestsOnBaseModeOn,
  onShowTmsLoads,
  onShowCardLoads,
  onShowBorders,
  onShowGpsView,
  onShowTmsView,
  onShowTachographView,
  onShowEvents,
  onGenerateTrips,
}: BusinessTripActionBarProps) => {
  const { t } = useLocalize()

  const handleGenerateTrips = React.useCallback(
    async (generationMode: number) => {
      if (onGenerateTrips) {
        await onGenerateTrips(generationMode)
      }
    },
    [onGenerateTrips],
  )

  const hasImports =
    onShowTmsLoads ||
    onShowCardLoads ||
    onShowBorders ||
    onShowGpsView ||
    onShowTmsView ||
    onShowTachographView

  return (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3.5">
      <div className="tw-flex tw-items-end tw-gap-5">
        {MARKER_TYPES.map((eventType, index) => (
          <div key={index}>
            <DelegationMarker
              canDrag={canDragMarker(eventType, events, groupMode) && !readonly}
              delegationMarker={{ ...newRow, tripEvent: eventType }}
              translate={t}
            />
          </div>
        ))}

        <DividerVertical />
        <Tooltip
          text={t('businessTrip.rests-on-base')}
          onClick={() => setIsRestsOnBaseModeOn(!isRestsOnBaseModeOn)}
        >
          <div
            className={`tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-md tw-p-1 ${isRestsOnBaseModeOn ? 'tw-bg-[#E4E9F7] tw-ring-[1px] tw-ring-primary' : ''} `}
          >
            <FaIcon
              icon={faHouseSolid}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>

        {hasImports && <DividerVertical />}

        {onShowTmsLoads && (
          <ImportButton
            text={t('businessTrip.tms-loads')}
            onClick={onShowTmsLoads}
          >
            <img src={TmsWhite} className="tw-h-4 tw-w-4 tw-min-w-4" />
          </ImportButton>
        )}

        {onShowCardLoads && (
          <ImportButton
            text={t('businessTrip.tms-card-loads')}
            onClick={onShowCardLoads}
          >
            <img src={TmsWhite} className="tw-h-4 tw-w-4 tw-min-w-4" />
          </ImportButton>
        )}

        {onShowGpsView && (
          <ImportButton
            text={t('newDelegation.import-gps')}
            onClick={onShowGpsView}
          >
            <FaIcon
              icon={faEarthAmericasSolid}
              className="tw-h-4 tw-w-4 tw-text-white"
            />
          </ImportButton>
        )}

        {onShowTmsView && (
          <ImportButton
            text={t('newDelegation.import-tms')}
            onClick={onShowTmsView}
          >
            <img src={TmsWhite} className="tw-h-4 tw-w-4 tw-min-w-4" />
          </ImportButton>
        )}

        {onShowTachographView && (
          <ImportButton
            text={t('newDelegation.import-from-tachograph')}
            onClick={onShowTachographView}
          >
            <FaIcon
              icon={faClockDeskSolid}
              className="tw-h-4 tw-w-4 tw-text-white"
            />
          </ImportButton>
        )}

        {onShowBorders && (
          <ImportButton
            text={t('businessTrip.borders')}
            onClick={onShowBorders}
          >
            <FaIcon icon={faGlobe} className="tw-h-4 tw-w-4 tw-text-white" />
          </ImportButton>
        )}

        {onGenerateTrips && (
          <>
            <DividerVertical />

            <span className="tw-text-sm tw-text-content-base">Generuj: </span>
            <div className="tw-flex tw-items-center tw-gap-4">
              <Tooltip
                text={t('groupedRibbon.dailyTitle')}
                onClick={() => handleGenerateTrips(TripGenerationMode.Daily)}
              >
                <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                  <FaIcon
                    icon={faCalendarDaysSolid}
                    className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                  />
                </div>
              </Tooltip>

              <Tooltip
                text={t('groupedRibbon.weeklyTitle')}
                onClick={() => handleGenerateTrips(TripGenerationMode.Weekly)}
              >
                <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                  <FaIcon
                    icon={faCalendarWeekSolid}
                    className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                  />
                </div>
              </Tooltip>

              <Tooltip
                text={t('groupedRibbon.monthManualEntryTitle')}
                onClick={() =>
                  handleGenerateTrips(TripGenerationMode.MonthManualEntry)
                }
              >
                <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                  <FaIcon
                    icon={faCalendarRangeSolid}
                    className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                  />
                </div>
              </Tooltip>

              <Tooltip
                text={t('groupedRibbon.monthManualEntry9Title')}
                onClick={() =>
                  handleGenerateTrips(TripGenerationMode.MonthManualEntry9)
                }
              >
                <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                  <CalendarIconWithBadge badge="9" />
                </div>
              </Tooltip>

              <Tooltip
                text={t('groupedRibbon.monthManualEntry24Title')}
                onClick={() =>
                  handleGenerateTrips(TripGenerationMode.MonthManualEntry24)
                }
              >
                <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                  <CalendarIconWithBadge badge="24" />
                </div>
              </Tooltip>

              <Tooltip
                text={t('groupedRibbon.monthManualEntry45Title')}
                onClick={() =>
                  handleGenerateTrips(TripGenerationMode.MonthManualEntry45)
                }
              >
                <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                  <CalendarIconWithBadge badge="45" />
                </div>
              </Tooltip>

              <DividerVertical />

              <Tooltip
                text={t('groupedRibbon.foreignTitle')}
                onClick={() => handleGenerateTrips(TripGenerationMode.Foreign)}
              >
                <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                  <FaIcon
                    icon={faTruckSolid}
                    className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                  />
                </div>
              </Tooltip>
            </div>
          </>
        )}

        {onShowEvents && (
          <>
            <DividerVertical />
            <Tooltip
              text={t('businessTrip.show-events-list')}
              onClick={onShowEvents}
            >
              <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-md tw-bg-primary/30 tw-px-2 tw-py-1 hover:tw-bg-primary/50">
                <FaIcon
                  icon={faKeyboard}
                  className="tw-h-4 tw-w-4 tw-text-white"
                />
                <span className="tw-text-xs tw-font-medium tw-text-white">
                  {t('businessTrip.events')}
                </span>
              </div>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  )
}

export default BusinessTripActionBar
